import * as THREE from 'three';
import {
  buildBaseCharacterSignature,
  cloneBaseCharacterAppearance,
  normalizeBaseCharacterAppearance,
  type BaseCharacterAppearance,
} from './baseCharacter';
import {
  applyCharacterAnimationSequence,
  rebuildCharacterBody,
  resolveEquipmentPresentationOverride,
  type CharacterAnimationStrategy,
  type CharacterAppearanceAssets,
  type CharacterAnimationSequence,
  type EquippedAppearanceItem,
  type EquipmentPresentationOverride,
} from './characterAppearance';
import {
  loadCharacterAppearanceAssets,
  loadPlayerAnimations,
  type PlayerAnimationSet,
} from './appearanceAssets';

/**
 * Equipment passed in by the host page (profile / equipment views).
 * Keys are equipment slot indices; values are the canonical RSPK item
 * id. Slots without an equipped item must be omitted (or set to null).
 */
export type PreviewEquipment = Partial<Record<number, EquippedAppearanceItem | null>>;

interface CharacterPreviewOptions {
  animationStrategy?: CharacterAnimationStrategy;
  presentationOverrides?: Record<string, EquipmentPresentationOverride>;
  autoRotate?: boolean;
  fixedRootYaw?: number;
  cameraDistance?: number;
  cameraPanY?: number;
  bob?: boolean;
  itemOnly?: boolean;
}

const DEFAULT_VIEW_ANGLE_RAD = Math.PI;
const DEFAULT_ROOT_YAW_OFFSET = 0.14;
const DEFAULT_CAMERA_DISTANCE = 6.4;
const MIN_CAMERA_DISTANCE = 3.6;
const MAX_CAMERA_DISTANCE = 9.2;
const ITEM_ONLY_DEFAULT_CAMERA_DISTANCE = 5.1;
const ITEM_ONLY_TARGET_Y = 0.16;
const ITEM_ONLY_MAX_DIMENSION = 2.45;
const ITEM_ONLY_MIN_SCALE = 1.25;
const ITEM_ONLY_MAX_SCALE = 4.2;
const ITEM_ONLY_ROTATION_START_DELAY = 1.25;
const ITEM_ONLY_ROTATION_SPEED = 0.72;
const CAMERA_TARGET = new THREE.Vector3(0, 0.6, 0);
const DRAG_RADIANS_PER_PIXEL = 0.012;
const DRAG_PAN_UNITS_PER_PIXEL = 0.0075;
const WHEEL_ZOOM_PER_PIXEL = 0.006;
const MAX_UPWARD_CAMERA_PAN = 0.9;
const tempItemBox = new THREE.Box3();
const tempItemSize = new THREE.Vector3();
const tempItemCenter = new THREE.Vector3();

export class CharacterCreationPreviewController {
  private readonly container: HTMLDivElement;
  private readonly options: CharacterPreviewOptions;
  private readonly resizeHandler = () => this.resize();
  private readonly pointerDownHandler = (event: PointerEvent) => this.onPointerDown(event);
  private readonly pointerMoveHandler = (event: PointerEvent) => this.onPointerMove(event);
  private readonly pointerUpHandler = (event: PointerEvent) => this.onPointerUp(event);
  private readonly wheelHandler = (event: WheelEvent) => this.onWheel(event);
  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  private root: THREE.Group | null = null;
  private body: THREE.Group | null = null;
  private frameHandle = 0;
  private mounted = false;
  private disposed = false;
  private lastAppearanceSignature = '';
  private equipment: PreviewEquipment = {};
  private currentAppearance: BaseCharacterAppearance | null = null;
  private animations: PlayerAnimationSet | null = null;
  private idleSequence: CharacterAnimationSequence | null = null;
  private autoRotate = true;
  private viewAngle = DEFAULT_VIEW_ANGLE_RAD;
  private cameraDistance = DEFAULT_CAMERA_DISTANCE;
  private cameraPanY = 0;
  private dragPointerId: number | null = null;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragStartAngle = DEFAULT_VIEW_ANGLE_RAD;
  private dragStartPanY = 0;
  private itemOnlyAutoStartYaw = DEFAULT_ROOT_YAW_OFFSET;
  private animationStartedAt = 0;

  constructor(container: HTMLDivElement, options: CharacterPreviewOptions = {}) {
    this.container = container;
    this.options = options;
    if (options.itemOnly === true) {
      this.cameraDistance = ITEM_ONLY_DEFAULT_CAMERA_DISTANCE;
      this.cameraPanY = 0;
    }
    if (typeof options.autoRotate === 'boolean') {
      this.autoRotate = options.autoRotate;
    }
    if (typeof options.cameraDistance === 'number' && Number.isFinite(options.cameraDistance)) {
      this.cameraDistance = clamp(options.cameraDistance, MIN_CAMERA_DISTANCE, MAX_CAMERA_DISTANCE);
    }
    if (typeof options.cameraPanY === 'number' && Number.isFinite(options.cameraPanY)) {
      this.cameraPanY = clamp(options.cameraPanY, 0, MAX_UPWARD_CAMERA_PAN);
    }
    if (typeof options.fixedRootYaw === 'number' && Number.isFinite(options.fixedRootYaw)) {
      this.viewAngle = normalizeAngle(options.fixedRootYaw - DEFAULT_ROOT_YAW_OFFSET + Math.PI);
      this.itemOnlyAutoStartYaw = options.fixedRootYaw;
    }
  }

  async mount(
    appearance: BaseCharacterAppearance,
    equipment: PreviewEquipment = {},
  ): Promise<void> {
    if (this.disposed) {
      return;
    }
    this.equipment = equipment;
    this.currentAppearance = cloneBaseCharacterAppearance(normalizeBaseCharacterAppearance(appearance));

    const [assets, animations] = await Promise.all([
      loadCharacterAppearanceAssets(),
      loadPlayerAnimations().catch(() => null),
    ]);
    if (this.disposed || this.mounted) {
      return;
    }
    this.animations = animations;
    if (animations) {
      this.idleSequence = animations.sequencesById[String(animations.standId)] ?? null;
    }

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color('#18120b');

    this.camera = new THREE.PerspectiveCamera(34, 1, 0.1, 50);
    this.updateCameraPosition();

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, preserveDrawingBuffer: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(this.container.clientWidth || 180, this.container.clientHeight || 230, false);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.domElement.style.width = '100%';
    this.renderer.domElement.style.height = '100%';
    this.renderer.domElement.style.display = 'block';
    this.renderer.domElement.style.cursor = 'grab';
    this.renderer.domElement.style.touchAction = 'none';
    this.renderer.domElement.addEventListener('pointerdown', this.pointerDownHandler);
    this.renderer.domElement.addEventListener('wheel', this.wheelHandler, { passive: false });
    this.container.replaceChildren(this.renderer.domElement);

    const hemi = new THREE.HemisphereLight('#d8d2c1', '#3d2c15', 1.55);
    this.scene.add(hemi);

    const key = new THREE.DirectionalLight('#fff0cc', 1.05);
    key.position.set(2.4, 3.2, 4.2);
    this.scene.add(key);

    const rim = new THREE.DirectionalLight('#9eb9d3', 0.34);
    rim.position.set(-2.2, 1.7, -2.6);
    this.scene.add(rim);

    const plinth = new THREE.Mesh(
      new THREE.CylinderGeometry(1.22, 1.4, 0.18, 20),
      new THREE.MeshStandardMaterial({
        color: '#65543a',
        roughness: 0.96,
        metalness: 0.04,
      }),
    );
    plinth.position.set(0, -1.05, 0);
    this.scene.add(plinth);

    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(1.18, 0.05, 8, 26),
      new THREE.MeshBasicMaterial({
        color: '#c1a66a',
        transparent: true,
        opacity: 0.35,
      }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.set(0, -0.95, 0);
    this.scene.add(ring);

    this.root = new THREE.Group();
    this.root.rotation.y = this.resolveRootYaw();
    this.body = new THREE.Group();
    if (this.options.itemOnly === true) {
      this.body.position.set(0, 0, 0);
      this.body.scale.setScalar(1);
    } else {
      this.body.position.set(0, -0.9, 0);
      this.body.scale.set(2.31, 2.1, 2.1);
    }
    this.root.add(this.body);
    this.scene.add(this.root);

    this.syncAppearance(appearance, assets);
    this.mounted = true;
    this.animationStartedAt = performance.now() / 1000;
    window.addEventListener('resize', this.resizeHandler);
    this.animate();
  }

  async updateAppearance(
    appearance: BaseCharacterAppearance,
    equipment?: PreviewEquipment,
  ): Promise<void> {
    if (this.disposed) {
      return;
    }
    this.currentAppearance = cloneBaseCharacterAppearance(normalizeBaseCharacterAppearance(appearance));
    if (equipment !== undefined) {
      this.equipment = equipment;
      // Equipment changed → invalidate the appearance signature so
      // syncAppearance re-runs even if the base appearance hasn't moved.
      this.lastAppearanceSignature = '';
    }

    const assets = await loadCharacterAppearanceAssets();
    if (this.disposed) {
      return;
    }

    if (!this.mounted) {
      await this.mount(appearance, this.equipment);
      return;
    }

    this.syncAppearance(appearance, assets);
  }

  async setPresentationOverrides(
    presentationOverrides: Record<string, EquipmentPresentationOverride> | undefined,
  ): Promise<void> {
    if (this.disposed) {
      return;
    }

    this.options.presentationOverrides = presentationOverrides;
    this.lastAppearanceSignature = '';
    if (!this.mounted || !this.currentAppearance) {
      return;
    }

    const assets = await loadCharacterAppearanceAssets();
    if (this.disposed) {
      return;
    }
    this.syncAppearance(this.currentAppearance, assets);
  }

  /** Update the worn loadout in-place without touching base appearance. */
  async setEquipment(equipment: PreviewEquipment): Promise<void> {
    this.equipment = equipment;
    this.lastAppearanceSignature = '';
    if (!this.mounted) return;
    const assets = await loadCharacterAppearanceAssets();
    if (this.disposed || !this.body) return;
    // Re-sync using the most recent appearance — read it back out of the
    // signature buffer when possible. Easier: caller invokes
    // updateAppearance() right after setEquipment with their current state.
  }

  dispose(): void {
    this.disposed = true;
    this.mounted = false;
    window.cancelAnimationFrame(this.frameHandle);
    window.removeEventListener('resize', this.resizeHandler);
    window.removeEventListener('pointermove', this.pointerMoveHandler);
    window.removeEventListener('pointerup', this.pointerUpHandler);
    window.removeEventListener('pointercancel', this.pointerUpHandler);
    if (this.renderer) {
      this.renderer.domElement.removeEventListener('pointerdown', this.pointerDownHandler);
      this.renderer.domElement.removeEventListener('wheel', this.wheelHandler);
    }
    this.dragPointerId = null;

    this.scene?.traverse((entry) => {
      if (!(entry instanceof THREE.Mesh)) {
        return;
      }
      entry.geometry.dispose();
      const material = entry.material;
      if (Array.isArray(material)) {
        material.forEach((part) => part.dispose());
        return;
      }
      material.dispose();
    });

    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
    }
    this.container.replaceChildren();
    this.renderer = null;
    this.scene = null;
    this.camera = null;
    this.root = null;
    this.body = null;
    this.currentAppearance = null;
    this.lastAppearanceSignature = '';
  }

  private syncAppearance(appearance: BaseCharacterAppearance, assets: CharacterAppearanceAssets): void {
    if (!this.body) {
      return;
    }

    const normalized = cloneBaseCharacterAppearance(normalizeBaseCharacterAppearance(appearance));
    this.currentAppearance = cloneBaseCharacterAppearance(normalized);
    // Combine appearance + equipment into the dedup signature so an
    // equip change re-renders even if the base appearance is unchanged.
    const equipmentTokens = Object.entries(this.equipment)
      .filter(([, v]) => v != null)
      .map(([slot, v]) => `${slot}:${(v as EquippedAppearanceItem).id}`)
      .sort()
      .join(',');
    const signature = buildBaseCharacterSignature(normalized) + '|eq:' + equipmentTokens;
    if (signature === this.lastAppearanceSignature) {
      return;
    }

    rebuildCharacterBody(this.body, assets, this.equipment, normalized, {
      animationStrategy: this.options.animationStrategy ?? 'static-pose',
      presentationOverrides: this.options.presentationOverrides,
      itemOnly: this.options.itemOnly,
    });
    this.idleSequence = this.resolveIdlePoseSequence(assets);
    this.applyIdlePose();
    this.fitItemOnlyBody();
    this.lastAppearanceSignature = signature;
  }

  private resolveIdlePoseSequence(assets: CharacterAppearanceAssets): CharacterAnimationSequence | null {
    if (!this.animations) {
      return null;
    }

    const standId = this.resolveStandAnimationId(assets);
    return this.animations.sequencesById[String(standId)]
      ?? this.animations.sequencesById[String(this.animations.standId)]
      ?? null;
  }

  private resolveStandAnimationId(assets: CharacterAppearanceAssets): number {
    const fallback = this.animations?.standId ?? 808;
    const weapon = this.resolvePrimaryWeaponEquipment(assets);
    if (!this.animations || !weapon) {
      return fallback;
    }

    const weaponAppearance = assets.manifest.items[String(weapon.id)];
    const overrideStand = weaponAppearance
      ? resolveEquipmentPresentationOverride(weaponAppearance.name, this.options.presentationOverrides)?.standAnimation
      : undefined;
    if (typeof overrideStand === 'number') {
      return overrideStand;
    }

    const itemStand = this.animations.weaponStandByItemId[String(weapon.id)];
    if (typeof itemStand === 'number') {
      return itemStand;
    }

    const weaponInterface = weaponAppearance?.weaponInterface;
    if (weaponInterface) {
      const interfaceStand = this.animations.weaponStandByInterface[weaponInterface];
      if (typeof interfaceStand === 'number') {
        return interfaceStand;
      }
    }

    return fallback;
  }

  private resolvePrimaryWeaponEquipment(assets: CharacterAppearanceAssets): EquippedAppearanceItem | null {
    const weapon = this.equipment[3];
    if (weapon) {
      return weapon;
    }

    const shield = this.equipment[5];
    if (!shield) {
      return null;
    }

    const appearance = assets.manifest.items[String(shield.id)];
    return appearance?.wearPos?.primary === 3 ? shield : null;
  }

  private applyIdlePose(): void {
    if (!this.body || !this.idleSequence) return;
    // Hold frame 0 of the idle sequence — that's the OSRS standing stance.
    // Loop=false + holdLastFrame=true keeps the pose static even though the
    // animator runs every frame. (Looping the full 12-frame breathing
    // animation looks great too if we ever want it; for static profile/
    // equipment renders frame 0 is sufficient and avoids subtle drift.)
    applyCharacterAnimationSequence(this.body, this.idleSequence, 0, {
      loop: false,
      holdLastFrame: true,
      strategy: this.options.animationStrategy ?? 'static-pose',
    });
  }

  private fitItemOnlyBody(): void {
    if (this.options.itemOnly !== true || !this.body || !this.camera) {
      return;
    }

    const savedRootRotation = this.root ? this.root.rotation.clone() : null;
    if (this.root) {
      this.root.rotation.set(0, 0, 0);
      this.root.updateMatrixWorld(true);
    }
    this.body.position.set(0, 0, 0);
    this.body.scale.setScalar(1);
    this.body.updateWorldMatrix(true, true);
    tempItemBox.setFromObject(this.body);
    if (tempItemBox.isEmpty()) {
      if (this.root && savedRootRotation) {
        this.root.rotation.copy(savedRootRotation);
        this.root.updateMatrixWorld(true);
      }
      return;
    }

    tempItemBox.getSize(tempItemSize);
    tempItemBox.getCenter(tempItemCenter);
    const maxDimension = Math.max(tempItemSize.x, tempItemSize.y, tempItemSize.z);
    if (!Number.isFinite(maxDimension) || maxDimension <= 0) {
      if (this.root && savedRootRotation) {
        this.root.rotation.copy(savedRootRotation);
        this.root.updateMatrixWorld(true);
      }
      return;
    }

    const scale = clamp(
      ITEM_ONLY_MAX_DIMENSION / maxDimension,
      ITEM_ONLY_MIN_SCALE,
      ITEM_ONLY_MAX_SCALE,
    );
    const target = new THREE.Vector3(0, ITEM_ONLY_TARGET_Y, 0);
    this.body.scale.setScalar(scale);
    this.body.position.copy(target).sub(tempItemCenter.multiplyScalar(scale));
    if (this.root && savedRootRotation) {
      this.root.rotation.copy(savedRootRotation);
      this.root.updateMatrixWorld(true);
    }

    const fittedHeight = Math.max(tempItemSize.y * scale, ITEM_ONLY_MAX_DIMENSION * 0.75);
    const fovRadians = THREE.MathUtils.degToRad(this.camera.fov);
    const distance = (fittedHeight * 0.5) / Math.tan(fovRadians * 0.5) / 0.78;
    this.cameraDistance = clamp(distance, MIN_CAMERA_DISTANCE, MAX_CAMERA_DISTANCE);
    this.updateCameraPosition();
  }

  private animate(): void {
    if (!this.renderer || !this.scene || !this.camera || !this.root || this.disposed) {
      return;
    }

    const elapsed = performance.now() / 1000;
    const rotationElapsed = Math.max(0, elapsed - this.animationStartedAt);
    this.root.rotation.y = this.autoRotate
      ? this.resolveAutoRootYaw(rotationElapsed)
      : this.resolveRootYaw();
    if (this.body && this.options.bob !== false) {
      this.body.position.y = -0.9 + Math.sin(elapsed * 1.45) * 0.018;
    }

    this.renderer.render(this.scene, this.camera);
    this.frameHandle = window.requestAnimationFrame(() => this.animate());
  }

  private resize(): void {
    if (!this.renderer || !this.camera) {
      return;
    }

    const width = this.container.clientWidth || 180;
    const height = this.container.clientHeight || 230;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  }

  private onPointerDown(event: PointerEvent): void {
    if (!this.renderer || event.button !== 0) {
      return;
    }

    this.stopAutoRotationPreservingView();
    this.dragPointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.dragStartAngle = this.viewAngle;
    this.dragStartPanY = this.cameraPanY;
    this.renderer.domElement.style.cursor = 'grabbing';
    this.renderer.domElement.setPointerCapture?.(event.pointerId);
    window.addEventListener('pointermove', this.pointerMoveHandler);
    window.addEventListener('pointerup', this.pointerUpHandler);
    window.addEventListener('pointercancel', this.pointerUpHandler);
  }

  private onPointerMove(event: PointerEvent): void {
    if (this.dragPointerId !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - this.dragStartX;
    const deltaY = event.clientY - this.dragStartY;
    this.viewAngle = normalizeAngle(this.dragStartAngle + deltaX * DRAG_RADIANS_PER_PIXEL);
    this.cameraPanY = clamp(
      this.dragStartPanY - deltaY * DRAG_PAN_UNITS_PER_PIXEL,
      0,
      MAX_UPWARD_CAMERA_PAN,
    );
    this.updateCameraPosition();
  }

  private onPointerUp(event: PointerEvent): void {
    if (this.dragPointerId !== event.pointerId) {
      return;
    }

    this.renderer?.domElement.releasePointerCapture?.(event.pointerId);
    if (this.renderer) {
      this.renderer.domElement.style.cursor = 'grab';
    }
    this.dragPointerId = null;
    window.removeEventListener('pointermove', this.pointerMoveHandler);
    window.removeEventListener('pointerup', this.pointerUpHandler);
    window.removeEventListener('pointercancel', this.pointerUpHandler);
  }

  private onWheel(event: WheelEvent): void {
    event.preventDefault();
    this.stopAutoRotationPreservingView();
    this.cameraDistance = clamp(
      this.cameraDistance + event.deltaY * WHEEL_ZOOM_PER_PIXEL,
      MIN_CAMERA_DISTANCE,
      MAX_CAMERA_DISTANCE,
    );
    this.updateCameraPosition();
  }

  private updateCameraPosition(): void {
    if (!this.camera) {
      return;
    }

    const target = this.options.itemOnly === true
      ? new THREE.Vector3(0, ITEM_ONLY_TARGET_Y, 0)
      : CAMERA_TARGET.clone();
    target.y += this.cameraPanY;
    const cameraY = this.options.itemOnly === true
      ? 0.6 + this.cameraPanY
      : 1.1 + this.cameraPanY;
    this.camera.position.set(0, cameraY, this.cameraDistance);
    this.camera.lookAt(target);
    this.camera.updateProjectionMatrix();
  }

  private resolveRootYaw(): number {
    if (typeof this.options.fixedRootYaw === 'number' && Number.isFinite(this.options.fixedRootYaw)) {
      return this.options.fixedRootYaw;
    }

    return this.viewAngle - Math.PI + DEFAULT_ROOT_YAW_OFFSET;
  }

  private resolveAutoRootYaw(elapsedSeconds: number): number {
    if (this.options.itemOnly === true) {
      return this.itemOnlyAutoStartYaw
        + Math.max(0, elapsedSeconds - ITEM_ONLY_ROTATION_START_DELAY) * ITEM_ONLY_ROTATION_SPEED;
    }

    return Math.sin(elapsedSeconds * 0.6) * 0.35 + DEFAULT_ROOT_YAW_OFFSET;
  }

  private stopAutoRotationPreservingView(): void {
    if (!this.autoRotate) {
      return;
    }

    this.autoRotate = false;
    if (this.root) {
      this.viewAngle = normalizeAngle(this.root.rotation.y - DEFAULT_ROOT_YAW_OFFSET + Math.PI);
    }
  }
}

function normalizeAngle(value: number): number {
  const fullTurn = Math.PI * 2;
  return ((value % fullTurn) + fullTurn) % fullTurn;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
